(ns assemble.templates-page.upload-modal.db
  (:require ["@zip.js/zip.js/lib/zip-no-worker-inflate.js" :as zip]
            [assemble.component.upload-modal :as upload-modal]
            [assemble.templates-page.validation :as validation]
            [assemble.util :as util]
            [clojure.string :as str]
            [re-frame.core :as rf]))

;; Even when using no-worker file, worker use needs to be
;; disabled in the configuration.
(zip/configure #js {:useWebWorkers false})

(defn get-selected-template [db]
  (get-in db [:templates-upload :selected-template]))

(defn get-pre-made-template-id [db]
  (get-in db [:templates-upload :pre-made-template-id]))

(rf/reg-event-fx ::open
  (fn [{:keys [db]} [_ {:keys [files mode selected-template]}]]
    {:db (assoc db :templates-upload {:modal-open true
                                      :mode mode
                                      :selected-template selected-template})
     :fx [[:dispatch [::set-queue files]]]}))

(rf/reg-event-db ::close
  (fn [db _]
    ;; Ignore close operation from modal if there is upload in progress.
    ;; Cancel button is additionally disabled visually.
    (let [queue (-> db :templates-upload :queue)
          active-upload (boolean (upload-modal/queue-first-by-status queue "uploading"))]
      (if active-upload
        db
        ;; NOTE: Later we might close the modal, but keep the queue
        ;; state in app db for background uploads.
        ;; Now the queue is also cleared when closing the modal.
        (dissoc db :templates-upload)))))

(defn parse-ad-size
  "Parse value like k=number,k2=number into a map."
  [s]
  (when s
    (->> (str/split s #",")
         (map (fn [x]
                (let [[k v] (str/split x #"=")
                      v (js/parseInt v)]
                  [(keyword (str/trim k)) (when-not (js/isNaN v) v)])))
         (into {}))))

(defn read-ad-size
  "Parse html document and get ad.size meta tag content."
  [html-source]
  (let [doc (.parseFromString (js/DOMParser.) html-source "text/html")
        ad-size (.querySelector doc "head meta[name='ad.size']")]
    (some-> ad-size
            .-content
            parse-ad-size)))

(def zip-file-exclusions #{"THUMBS.DB" ".DS_STORE" "__MACOSX"})

(defn- sanitise-zip-file-entries [entries]
  (remove (fn [^js entry]
            (or (.-directory entry)
                (some (fn [exclusion]
                        (str/includes? (str/upper-case (.-filename entry)) exclusion))
                      zip-file-exclusions)))
          entries))

(defn read-template [{:keys [item advertiser-id on-done on-item selected-template]}]
  (let [{:keys [file]} item
        rdr (zip/ZipReader. (zip/BlobReader. file))]
    (-> (.getEntries rdr)
        (.then (fn [entries]
                 (let [sanitised-entries (sanitise-zip-file-entries entries)
                       index-entry (some (fn [entry] (when (= "index.html" (.-filename entry)) entry)) sanitised-entries)
                       aep-entry (some (fn [entry] (when (str/ends-with? (.-filename entry) ".aep") entry)) sanitised-entries)
                       template-json-entry (some (fn [entry] (when (= "template.json" (.-filename entry)) entry)) sanitised-entries)
                       zip-paths (distinct (mapv #(.-filename %) sanitised-entries))]
                   (if index-entry
                     (-> (js/Promise.all [(.getData index-entry (zip/TextWriter.))
                                          (try (.getData template-json-entry (zip/TextWriter.))
                                               (catch js/Object _
                                                 :cannot-read-template-json))])
                         (.then (fn [[index-source json-source]]
                                  (let [validation-results [(validation/validate-zip-paths validation/paths-schema zip-paths)]
                                        ad-size (read-ad-size index-source)
                                        final-validation (->> [(validation/validate-ad-size ad-size)
                                                               (validation/validate-fonts index-source advertiser-id)
                                                               (validation/validate-template-json json-source)
                                                               (validation/validate-template-type (:template-type selected-template) "html")]
                                                              (concat validation-results)
                                                              (keep identity))]
                                    (if (seq final-validation)
                                      (do
                                        ;; Display validation messages in console until we can display them in the UI
                                        (js/console.log "Template validation failure:" (.-name file))
                                        (js/console.log (clj->js final-validation))
                                        (->> (assoc ad-size :validation final-validation)
                                             (on-item "html")))
                                      (on-item "html" ad-size)))))
                         (.catch (fn [e]
                                   (js/console.warn e)
                                   (on-item nil nil))))
                     (if aep-entry
                       (-> (js/Promise.all [(try (.getData template-json-entry (zip/TextWriter.))
                                                 (catch js/Object _
                                                   :cannot-read-template-json))])
                           (.then (fn [[json-source]]
                                    (let [validation-results [(validation/validate-zip-paths validation/ae-paths-schema zip-paths)]
                                          final-validation (->> [(validation/validate-ae-zip-paths (mapv #(.-filename %) sanitised-entries))
                                                                 (validation/validate-template-json json-source)
                                                                 (validation/validate-template-type (:template-type selected-template) "after-effects")]
                                                                (concat validation-results)
                                                                (keep identity))
                                          meta (when (seq final-validation)
                                                 ;; Display validation messages in console until we can display them in the UI
                                                 (js/console.log "Template validation failure:" (.-name file))
                                                 (js/console.log (clj->js final-validation))
                                                 {:validation final-validation})]
                                      (rf/dispatch [::set-pre-made-template-id
                                                    (get (js->clj (js/JSON.parse json-source)) "pre_made_template_id")])
                                      (on-item "after-effects" meta)))))
                       (on-item "unknown" {:validation [{:message (validation/unknown-template-type-message)}]}))))))
        (.finally (fn []
                    (.close rdr)
                    (on-done))))))

(rf/reg-event-db ::set-pre-made-template-id
  (fn [db [_  pre-made-template-id]]
    (assoc-in db [:templates-upload :pre-made-template-id] pre-made-template-id)))

(rf/reg-event-db ::template-detected
  (fn [db [_ id template-type {:keys [validation] :as metadata}]]
    (update-in db [:templates-upload :queue id] assoc
               :status (cond
                         validation "invalid"
                         template-type "queue"
                         :else "unsupported")
               :template-type template-type
               :metadata metadata)))

(rf/reg-event-fx ::read-zip-files
  [(rf/inject-cofx :sub :context)]
  (fn [{:keys [db] {:keys [advertiser-id]} :context} _]
    (let [queue (:queue (:templates-upload db))
          item (upload-modal/queue-first-by-status queue "read-zip")]
      (when item
        (read-template {:item item
                        :advertiser-id advertiser-id
                        :selected-template (get-selected-template db)
                        :on-item #(rf/dispatch [::template-detected (:id item) %1 %2])
                        :on-done #(rf/dispatch [::read-zip-files])})
        {:db (assoc-in db [:templates-upload :queue (:id item) :status] "reading-zip")}))))

(rf/reg-event-fx ::set-queue
  (fn [{:keys [db]} [_ files]]
    (let [queue (upload-modal/files->queue
                 files
                 (fn [{:keys [filename]}]
                   (contains? #{"zip"} (util/file-extension filename)))
                 (fn [x]
                   (update x :status #(if (not= % "unsupported") "read-zip" %))))]
      {:db (assoc-in db [:templates-upload :queue] queue)
       :fx [[:dispatch [::read-zip-files]]]})))

(rf/reg-event-db ::remove-queue-item
  (fn [db [_ id]]
    (update-in db [:templates-upload :queue] upload-modal/remove-queue-item id)))

(rf/reg-event-db ::set-template-option
  (fn [db [_ v]]
    (assoc-in db [:templates-upload :selected-template] v)))

(rf/reg-sub ::get-queue
  (fn [db]
    (:queue (:templates-upload db))))

(rf/reg-sub ::modal-open
  (fn [db]
    (boolean (:modal-open (:templates-upload db)))))

(rf/reg-sub ::props
  (fn [db]
    (:templates-upload db)))
